import {
  Box,
  CircularProgress,
  FormControlLabel,
  Typography,
} from "@mui/material";
import Divider from "@mui/material/Divider";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import TaxExceptionModal from "../tax-exception-modal/TaxExceptionModal";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { Colors } from "../../../configs/colors";
import IOSSwitch from "../../atoms/custom-toggle-button/custom-toggle-button";
import { cloneDeep, debounce } from "lodash";

import { TreeView } from "@mui/x-tree-view";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ParentNode from "../../atoms/tree-view/parentNode";
import ChildNode from "../../atoms/tree-view/childNode";
import CustomDateTimePicker from "../../atoms/custom-date-time-picker/custom-date-time-picker";
import dayjs, { Dayjs } from "dayjs";
import { upperFirst } from "lodash";
import { CustomButton, CustomTextBox } from "../../atoms";
import useCart from "../../../hooks/useCart";
import { formatCurrency } from "../../../util/common-functions";
import { pxToRem } from "../../../util";
import { useParams } from "react-router-dom";
import { useSpecialDate } from "../../../hooks/useSpecialDate";
import { date } from "yup";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";
import { validatePinTagsAction } from "../../../redux/actions/pintagAction";
import { addSession } from "../../../redux/actions/cartAction";
import PinTagDrawer from "../../../pages/pos/customer/PinTagDrawer";

export interface IPaymentModalData {
  paymentMode: string;
  paidAmount: number;
  // The amount actually typed/keyed into the Cash "Paid" field, before
  // payment.tsx's handleOnCheckout folds `trackrem` (the bill's already-
  // collected amount) into `paidAmount` for the backend's own settlement
  // math - a "Cash Received"/change-due confirmation needs this raw value
  // instead, or the displayed change is off by whatever was already paid
  // on the bill. Optional so existing callers that don't care are unaffected.
  rawPaidAmount?: number;
  checkNumber?: string;
  isStoreCredit?: boolean;
}

interface ISessionDate {
  id: string;
  specialDate: Dayjs | null;
}
interface ISessionTime {
  id: string;
  specialTime: Dayjs | null;
}
interface ISessionSwitch {
  id: string;
  checked: boolean;
}
export interface ICheckInSummaryProps {
  onCheckout(data: IPaymentModalData): void;
  masterSwitch: boolean;
  sessionSwitches: ISessionSwitch[];
  masterDate: Dayjs | null;
  masterTime: Dayjs | null;
  sessionDates: ISessionDate[];
  sessionTimes: ISessionTime[];
  isModalOpen?: boolean;
  // setSessionDates(prevSessionDates:ISessionDate[]): void;
  // setSessionTimes(prevSessionTimes:ISessionTime[]): void;
  // setSessionSwitches(prevSessionSwitches:ISessionSwitch[]): void;
  // setMasterSwitch(prevState: any): void;
  // setMasterDate(date: Dayjs | null): void;
  // setMasterTime(time: Dayjs | null): void;
  handleOnChangeMasterSwitch(): void;
  handleOnChangeMasterDate(date: Dayjs | null): void;
  handleOnChangeMasterTime(time: Dayjs | null): void;
  handleOnChangeSessionSwitch(id: string): void;
  handleOnChangeSessionDate(id: string, date: Dayjs | null): void;
  handleOnChangeSessionTime(id: string, time: Dayjs | null): void;
}

const CheckInSummary: FC<ICheckInSummaryProps> = ({
  onCheckout,
  masterSwitch,
  sessionSwitches,
  masterDate,
  masterTime,
  sessionDates,
  sessionTimes,
  isModalOpen = true,
  // setSessionDates,
  // setSessionTimes,
  // setSessionSwitches,
  // setMasterSwitch,
  // setMasterDate,
  // setMasterTime
  handleOnChangeMasterSwitch,
  handleOnChangeMasterDate,
  handleOnChangeMasterTime,
  handleOnChangeSessionSwitch,
  handleOnChangeSessionDate,
  handleOnChangeSessionTime,
}) => {
  const { eligibleDiscounts } = useAppSelector((state) => state.discount);
  const {
    cartTotal,
    getSessionTotalBySessionId: getSessionTotal,
    getSessionItemCount,
    getCartItemPrice,
    totalDiscountAmount,
  } = useCart();
  const params = useParams();
  const dispatch = useAppDispatch();
  const snackbar = useSnackbarContext();
  const billId = params.billId;
  const [selectedPaymentMode, setSelectedPaymentMode] =
    useState<string>("CASH");
  const [paidAmount, setPaidAmount] = useState<number>(0);
  const { customer } = useAppSelector((state) => state.customer);
  const taxEnabled =
    useAppSelector(
      (state) => state.merchant.merchantListResponse?.body?.[0]?.taxEnabled
    ) ?? false;
  const [isTaxModalOpen, setIsTaxModalOpen] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    null
  );
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [resetTagsTrigger, setResetTagsTrigger] = useState(0);
  const [isPinTagDrawerOpen, setIsPinTagDrawerOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const [resetFlipIndex, setResetFlipIndex] = useState<number | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const previousTagsRef = useRef<Record<string, string[]>>({});
  const { activeStationCashier } = useAppSelector(
    (state) => state.cashierStation
  );

  const { cart, editBillLoading, selectedSession } = useAppSelector(
    (state) => state.cart
  );

  const { getColorPintags } = useAppSelector((state) => state.pintag);

  const aliasToColor = (alias?: string | null): string | undefined => {
    if (!alias) return undefined;
    const match = getColorPintags?.body?.find((c) => c.alias === alias);
    return match?.color;
  };

  //console.log(getColorPintags?.body);

  const totalItemCount = cart.reduce((acc, item) => {
    return acc + item.cartItems.length;
  }, 0);

  const handleOnClickCheckout = () => {
    const data: IPaymentModalData = {
      paymentMode: selectedPaymentMode,
      paidAmount: paidAmount,
    };
    onCheckout(data);
  };
  const sessionInfo = cart.map((session) => ({
    sessionId: session.sessionId,
    date: session.pinTag?.date ?? "No date",
  }));

  const { date: specialDate } = useSpecialDate();

  useEffect(() => {
    if (!specialDate || cart.length === 0 || sessionSwitches.length === 0)
      return;

    cart.forEach((session) => {
      const sessionDate = session.pinTag?.date;
      const isSameDay =
        sessionDate && dayjs(sessionDate).isSame(specialDate, "day");

      const isAlreadyOn = sessionSwitches.find(
        (s) => s.id === session.sessionId
      )?.checked;

      if (isSameDay && !isAlreadyOn) {
        handleOnChangeSessionSwitch(session.sessionId);
      }
    });
  }, [cart, sessionSwitches, specialDate]);

  const date1 = dayjs(sessionInfo[0]?.date);
  const date2 = dayjs(sessionDates[0]?.specialDate);

  const resolvePinMeta = (
    colorArg: string | null,
    aliasArg?: string,
    colorIdArg?: number
  ) => {
    const list = getColorPintags?.body ?? [];
    const lc = (v?: string | null) => (v ?? "").toLowerCase();

    const meta =
      list.find((t) => lc(t.alias) === lc(aliasArg)) ||
      list.find((t) => lc(t.color) === lc(colorArg)) ||
      list.find((t) => lc(t.description) === lc(aliasArg));

    return {
      color: meta?.color ?? colorArg ?? null,
      alias: meta?.alias ?? aliasArg ?? null,
      colorId: meta?.id ?? colorIdArg,
      day: meta?.day ? String(meta.day).toUpperCase() : undefined,
    };
  };

  const debouncedAssignPinTag = useMemo(
    () =>
      debounce(
        async (
          color: string | null,
          tags: string[],
          textBoxCount: number,
          colorId?: number,
          alias?: string,
          day?: string,
          date?: string | null
        ) => {
          if (!selectedSessionId) return;

          const meta = resolvePinMeta(color, alias, colorId);
          const targetTagList = tags.slice(0, textBoxCount);
          const isIncomplete = targetTagList.some((t) => !t || t.length !== 3);

          setIsValidating(true);
          if (meta.color === null || !targetTagList.length || isIncomplete) {
            setIsValidating(false);
            return;
          }

          try {
            const res = await dispatch(
              validatePinTagsAction({
                pintagColor: meta.color,
                pintagColorId: meta.colorId,
                pintagAlias: meta.alias ?? undefined,
                tags: targetTagList,
                day: meta.day ?? day,
              })
            ).unwrap();

            const ok = res?.response?.valid;
            const errorMessage =
              res?.response?.errorMessage || "Pin tag validation failed";

            if (!ok) {
              setResetTagsTrigger((prev) => prev + 1);
              const unavailable = res?.response?.unavailableTags?.[0];
              const invalidIdx = targetTagList.findIndex(
                (t) => t === unavailable
              );
              if (invalidIdx !== -1) setResetFlipIndex(invalidIdx);
              snackbar.showSnackbar(errorMessage, "error");
              return;
            }

            const sessionClone = cloneDeep(
              cart.find((s) => s.sessionId === selectedSessionId)
            );
            if (sessionClone) {
              sessionClone.pinTag = {
                color: meta.color,
                tags: targetTagList,
                date: date || selectedSession?.pinTag?.date || null,
              };
              dispatch(addSession(sessionClone));
            }
          } catch {
            snackbar.showSnackbar("Validation failed", "error");
          } finally {
            setIsValidating(false);
          }
        },
        300
      ),

    [cart, dispatch, selectedSessionId, snackbar, getColorPintags?.body]
  );

  useEffect(() => {
    if (selectedSessionId) {
      const session = cart.find((s) => s.sessionId === selectedSessionId);
      if (session?.pinTag?.tags) {
        previousTagsRef.current[selectedSessionId] = [...session.pinTag.tags];
      }
    }
  }, [selectedSessionId, cart]);

  const handleTypingStart = () => setIsValidating(true);

  // Reset drawer state when modal closes
  useEffect(() => {
    if (!isModalOpen) {
      setIsPinTagDrawerOpen(false);
      setSelectedSessionId(null);
    }
  }, [isModalOpen]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "1.21428571rem",
        mt: { xs: "-3rem", sm: "0" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "1.78571rem",
          alignItems: "center",
        }}
      >
        {/* <Box sx={{ width: "48.6428571rem", height: "2.21428571rem" }}></Box> */}
        <Box
          sx={{
            ml: "auto",
            width: "33.5rem",
            height: "3.57142857rem",
            background: Colors.BACKGROUND_GRAY,
            borderRadius: "0.5rem",
            py: "0.964285714rem",
            px: "1.75rem",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "0.714285714rem",
          }}
        >
          <FormControlLabel
            control={<IOSSwitch />}
            checked={masterSwitch}
            onChange={handleOnChangeMasterSwitch}
            label={
              <Typography
                sx={{
                  fontSize: "1.42857143rem",
                  lineHeight: "1.71428571rem",
                  fontWeight: 400,
                  mr: "0.964285714rem",
                }}
              >
                Special
              </Typography>
            }
            labelPlacement="start"
            disabled={true}
            sx={{ margin: 0, mr: "1.22214286rem" }}
          />
          <CustomDateTimePicker
            type="date"
            value={masterDate ? specialDate : date1}
            onChange={handleOnChangeMasterDate}
            disabled={true}
          />
          <CustomDateTimePicker
            type="time"
            value={masterTime}
            onChange={handleOnChangeMasterTime}
            disabled={true}
          />
        </Box>
      </Box>
      <Box
        sx={{
          background: Colors.WHITE,
          width: "100%",
          height: "23rem",
          borderRadius: "0.5rem",
          border: `0.0714285714rem solid ${Colors.ACCENT_GRAY}`,
          padding: "1.14285714rem",
          boxSizing: "border-box",
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: "0.5rem" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ADADAD",
            borderRadius: "0rem",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
            borderRadius: "0rem",
          },
        }}
      >
        <TreeView
          aria-label="customized"
          defaultExpanded={["1"]}
          defaultExpandIcon={<ExpandMoreIcon />}
          defaultCollapseIcon={<ExpandLessIcon />}
          sx={{ overflowX: "hidden" }}
          onNodeToggle={() => false} // Disable expansion
        >
          {cart?.map((item, index) => {
            return (
              <ParentNode
                key={item.sessionId}
                nodeID={item.sessionId}
                service={
                  item?.cartItems[0]?.serviceType?.categoryName +
                  "-" +
                  ("00" + (index + 1)).slice(-3) || item.sessionId
                }
                qty={getSessionItemCount(item.sessionId) ?? 0}
                total={getSessionTotal(item.sessionId)}
                special={sessionSwitches[index]?.checked}
                onChangeSpecial={() =>
                  handleOnChangeSessionSwitch(item.sessionId)
                }
                specialDate={
                  date1.isSame(date2, "day")
                    ? sessionDates[index]?.specialDate
                    : date1
                }
                specialTime={sessionTimes[index]?.specialTime}
                onChangeSpecialDate={(date) =>
                  handleOnChangeSessionDate(item.sessionId, date)
                }
                onChangeSpecialTime={(time) =>
                  handleOnChangeSessionTime(item.sessionId, time)
                }
                masterSpecial={masterSwitch}
                onClick={() => {
                  if (selectedSessionId === item.sessionId) {
                    setSelectedSessionId(null);
                    setIsPinTagDrawerOpen(false);
                  } else {
                    setSelectedSessionId(item.sessionId);
                    setIsPinTagDrawerOpen(true);
                  }
                }}
                statusColor={item.pinTag?.color || undefined}
              >
                {item.cartItems.map((child, index) => {
                  return (
                    <ChildNode
                      key={child.itemId}
                      nodeID={child.itemId}
                      qty={child.qty}
                      total={getCartItemPrice(item.sessionId, index)}
                      items={[
                        child.garmentType?.name,
                        child.garmentStyle?.name,
                        child.materialType?.name,
                        child.garmentColor
                          ?.map((color) => upperFirst(color.toLowerCase()))
                          .join(", "),
                        child.texture,
                      ]}
                      sx={{
                        "& .MuiIconButton-root": {
                          display: "none",
                        },
                        "& .MuiTypography-root:first-of-type": {
                          marginLeft: "1.5rem",
                        },
                      }}
                    />
                  );
                })}
              </ParentNode>
            );
          })}
        </TreeView>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          padding: "0.928571429rem 2.17857143rem",
          background: Colors.ACCENT_GRAY,
          borderRadius: "0.5rem",
        }}
      ></Box>
      <Divider
        variant="middle"
        sx={{
          width: "63.9285714rem",
          alignSelf: "center",
          height: "0.285714286rem",
          borderRadius: "1rem",
          background: Colors.ACCENT_GRAY,
        }}
      />
      <Box
        ref={modalRef}
        sx={{
          height: "17.2857143rem",
          display: "flex",
          flexDirection: "row",
          gap: "1.21428571rem",
          position: "relative",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "0.785714286rem",
            flexGrow: 1,
            width: "5rem",
          }}
        ></Box>
        <Divider
          orientation="vertical"
          variant="middle"
          sx={{
            width: "0.285714286rem",
            alignSelf: "center",
            height: "15rem",
            borderRadius: "1rem",
            background: Colors.ACCENT_GRAY,
          }}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            width: pxToRem(528.8248),
            height: "17.2857143rem",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "0.357142857rem",
            }}
          >
            <Box
              sx={{
                height: "7.21428571rem",
                background: Colors.BACKGROUND_GRAY,
                borderRadius: "0.571428571rem",
                display: "flex",
                flexDirection: "column",
                px: "1.41071429rem",
                py: "0.4rem",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  mt: 1.2,
                  alignItems: "center",
                }}
              >
                <Typography sx={{ fontSize: "1.42857143rem" }}>
                  Total
                </Typography>
                <Typography>
                  {formatCurrency({
                    amount: cartTotal,
                    prefix: "$",
                  })}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography sx={{ fontSize: "1.42857143rem" }}>
                  Total Discount
                </Typography>
                <Typography>
                  {formatCurrency({
                    amount: totalDiscountAmount,
                    prefix: "$",
                  })}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                height: "2.57142857rem",
                background: Colors.BACKGROUND_GRAY,
                borderRadius: "0.571428571rem",
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                px: "1.41071429rem",
                py: "0.285714286rem",
              }}
            >
              <Typography
                sx={{ fontWeight: "bold", fontSize: "1.42857143rem" }}
              >
                Net Total:
              </Typography>
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: "1.71428571rem",
                }}
              >
                {formatCurrency({
                  amount: cartTotal - totalDiscountAmount,
                  prefix: "$",
                })}
              </Typography>
            </Box>
          </Box>
          <CustomButton
            onClick={taxEnabled ? () => setIsTaxModalOpen(true) : undefined}
            disabled={!taxEnabled}
            sx={{
              background: "none",
              border: `0.0714285714rem solid ${taxEnabled ? Colors.SECONDARY : Colors.TEXT_GRAY_DARK}`,
              color: taxEnabled ? Colors.SECONDARY : Colors.TEXT_GRAY_DARK,
              textTransform: "capitalize",
              height: "3.5rem",
              fontSize: "1.28571429rem",
              width: "100%",
              fontWeight: "bold",
              borderRadius: "0.714285714rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              opacity: taxEnabled ? 1 : 0.4,
              mb: "0.714285714rem",
              "&:hover": {
                background: taxEnabled ? `${Colors.SECONDARY}1A` : "none",
              },
            }}
            name="tax-exemption-button"
          >
            <ReceiptIcon sx={{ fontSize: "1.5rem" }} />
            Tax Exemption
          </CustomButton>
          <CustomButton
            onClick={handleOnClickCheckout}
            sx={{
              background: Colors.PRIMARY,
              color: Colors.WHITE,
              textTransform: "capitalize",
              height: "5.85714286rem",
              fontSize: "2.57142857rem",
              width: "100%",
              fontWeight: "bold",
              borderRadius: "0.714285714rem",
              "&:hover": {
                background: Colors.PRIMARY,
              },
            }}
            name="proceed-button"
            disabled={editBillLoading || !activeStationCashier}
          >
            {editBillLoading ? (
              <CircularProgress size="1.71428571rem" sx={{ color: Colors.WHITE }} />
            ) : billId ? (
              "Update Bill"
            ) : (
              "Proceed to Payment"
            )}
          </CustomButton>
        </Box>
      </Box>

      <TaxExceptionModal
        open={isTaxModalOpen}
        onClose={() => setIsTaxModalOpen(false)}
        customerId={customer?.id ?? 0}
      />

      <PinTagDrawer
        isOpen={isPinTagDrawerOpen}
        toggleDrawer={() => setIsPinTagDrawerOpen(!isPinTagDrawerOpen)}
        containerRef={modalRef.current}
        selectedSessionId={selectedSessionId}
        serviceTypeId={
          cart.find((item) => item.sessionId === selectedSessionId)
            ?.cartItems[0]?.serviceType?.id ?? 0
        }
        itemsCount={
          cart
            .find((item) => item.sessionId === selectedSessionId)
            ?.cartItems.reduce((acc, item) => {
              return item.finalized ? acc + item.qty : acc;
            }, 0) ?? 0
        }
        initialColor={
          cart.find((item) => item.sessionId === selectedSessionId)
            ?.pinTag?.color ?? undefined
        }
        initialDate={
          cart.find((item) => item.sessionId === selectedSessionId)
            ?.pinTag?.date ?? undefined
        }
        initialTags={
          cart.find((item) => item.sessionId === selectedSessionId)
            ?.pinTag?.tags ?? undefined
        }
        onTypingStart={handleTypingStart}
        resetTagsTrigger={resetTagsTrigger}
        resetFlipIndex={resetFlipIndex}
        setResetFlipIndex={setResetFlipIndex}
        onCalendarOpenChange={(isOpen) => setIsCalendarOpen(isOpen)}
      />
    </Box>
  );
};

export default CheckInSummary;
